// useKeyboardShortcuts.ts
import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface KeyboardShortcut {
  key: string | string[];
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  handler: (event: KeyboardEvent) => void;
  description?: string;
}

export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[], enabled: boolean = true) => {
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Ignore if user is typing in an input or textarea
    if (event.target instanceof HTMLInputElement || 
        event.target instanceof HTMLTextAreaElement) {
      return;
    }

    shortcuts.forEach(shortcut => {
      const keys = Array.isArray(shortcut.key) ? shortcut.key : [shortcut.key];
      const keyPressed = keys.some(key => 
        event.key.toLowerCase() === key.toLowerCase() ||
        event.code.toLowerCase() === key.toLowerCase()
      );

      if (keyPressed &&
          (!shortcut.ctrl || event.ctrlKey) &&
          (!shortcut.shift || event.shiftKey) &&
          (!shortcut.alt || event.altKey)) {
        event.preventDefault();
        shortcut.handler(event);
      }
    });
  }, [shortcuts, enabled]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);
};

// Navigation shortcuts with "G then X" pattern
export const useNavigationShortcuts = () => {
  const navigate = useNavigate();
  const [waitingForSecondKey, setWaitingForSecondKey] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleNavigation = useCallback((destination: string) => {
    navigate(destination);
    setWaitingForSecondKey(false);
  }, [navigate]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input or textarea
      if (event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Disable on quiz pages - they have their own G shortcut for "Go to Question"
      if (window.location.pathname.startsWith('/quiz/') ||
          window.location.pathname.startsWith('/quiz-old/')) {
        return;
      }

      // First key: G
      if (event.key.toLowerCase() === 'g' && !waitingForSecondKey) {
        event.preventDefault();
        setWaitingForSecondKey(true);
        
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // Reset after 2 seconds
        timeoutRef.current = setTimeout(() => {
          setWaitingForSecondKey(false);
        }, 2000);
        
        return;
      }

      // Second key handling
      if (waitingForSecondKey) {
        event.preventDefault();
        
        switch (event.key.toLowerCase()) {
          case 'h':
            handleNavigation('/home');
            break;
          case 's':
            handleNavigation('/study');
            break;
          case 'r':
            handleNavigation('/review');
            break;
          case 'a':
            handleNavigation('/analytics');
            break;
          case 'b':
            handleNavigation('/bookmarks');
            break;
          default:
            // Invalid second key, reset
            setWaitingForSecondKey(false);
        }
        
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [waitingForSecondKey, handleNavigation]);

  return { waitingForSecondKey };
};

// Global keyboard shortcuts that work across the app
export const useGlobalKeyboardShortcuts = () => {
  const shortcuts: KeyboardShortcut[] = [
    {
      key: 't',
      handler: () => {
        // Toggle theme
        const themeToggleButton = document.querySelector('[aria-label="Toggle theme"]') as HTMLButtonElement;
        if (themeToggleButton) {
          themeToggleButton.click();
        }
      },
      description: 'Toggle dark/light theme'
    },
    {
      key: '?',
      shift: true,
      handler: () => {
        // Show keyboard shortcuts help
        const event = new CustomEvent('showKeyboardHelp');
        window.dispatchEvent(event);
      },
      description: 'Show keyboard shortcuts'
    }
  ];

  useKeyboardShortcuts(shortcuts);
  
  // Also use navigation shortcuts
  const { waitingForSecondKey } = useNavigationShortcuts();
  
  return { waitingForSecondKey };
};